import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { portal } from "../../lib/api";
import { BrokerDisclosureCompact } from "../../components/BrokerDisclosure";

const AGENT_KEY = "realtor_os_portal_agent";
const DISCLOSURE_KEY = "realtor_os_portal_disclosure";

/**
 * PortalChat — the client-side "General" (transaction-less) thread with
 * their agent. Distinct from the per-transaction chat because it stays
 * usable even before any deal is under contract.
 *
 * Backend endpoints:
 *   GET  /portal/messages/general   — thread
 *   POST /portal/messages/general   — send
 */
export default function PortalChat() {
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [disclosure, setDisclosure] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(AGENT_KEY);
      setAgent(raw ? JSON.parse(raw) : null);
      const dRaw = localStorage.getItem(DISCLOSURE_KEY);
      const cached = dRaw ? JSON.parse(dRaw) : null;
      setDisclosure(cached);
      // Lazy fetch when the cache is empty (e.g. a fresh impersonation
      // session that predates the disclosure cache-write). Ensures every
      // client-facing chat still renders the compliance block.
      if (!cached) {
        portal.get("/portal/me").then(r => {
          if (r.data?.disclosure) {
            setDisclosure(r.data.disclosure);
            try { localStorage.setItem(DISCLOSURE_KEY, JSON.stringify(r.data.disclosure)); } catch { /* quota */ }
          }
        }).catch(() => {});
      }
    } catch (e) { console.debug("agent parse failed", e); }
  }, []);

  const load = async () => {
    try {
      const { data } = await portal.get("/portal/messages/general");
      setThread(data || []);
    } catch (e) { console.debug("load thread failed", e); }
  };

  useEffect(() => {
    load();
    // Poll for new messages every 12s so the client sees the agent's replies
    // without a refresh. Cheap enough for a stack that isn't real-time.
    const id = setInterval(load, 12_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await portal.post("/portal/messages/general", { body: text });
      setBody("");
      await load();
    } catch {
      toast.error("Message could not be sent");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => nav("/portal")}
            className="h-9 w-9 rounded-md hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center"
            aria-label="Back to portal"
            data-testid="portal-chat-back"
          >
            <ArrowLeft className="h-5 w-5 text-slate-700" />
          </button>
          {agent?.photo_url ? (
            <img src={agent.photo_url} alt={agent.full_name} className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200" />
          ) : (
            <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: "var(--primary-hex)" }}>
              {(agent?.full_name || "?").split(" ").slice(0, 2).map(s => s[0]).join("")}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-heading font-semibold text-slate-900 text-sm sm:text-base truncate" data-testid="portal-chat-agent-name">{agent?.full_name || "Your agent"}</div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">General</div>
          </div>
        </div>
        {/* Broker disclosure — Compact variant sits INSIDE the header,
            directly under the agent name. State advertising rules require
            the employing brokerage to be identified alongside the agent on
            every client-facing surface, at least as prominently. */}
        {disclosure && (
          <div className="px-4 sm:px-6 pb-3">
            <BrokerDisclosureCompact disclosure={disclosure} />
          </div>
        )}
      </header>

      {/* Scroll-locked thread: only this region scrolls; header + composer are fixed. */}
      <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-2" data-testid="portal-chat-thread">
          {thread.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-16">
              Say hello. Anything you send here goes straight to {agent?.full_name || "your agent"}.
            </div>
          )}
          {thread.map((m, i) => {
            const isMine = m.sender === "client";
            const prev = thread[i - 1];
            const startOfRun = !prev || prev.sender !== m.sender;
            return (
              <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"} ${startOfRun ? "mt-3" : ""}`}>
                <div className={`max-w-[80%] sm:max-w-md rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                  isMine ? "bg-slate-900 text-white rounded-br-md" : "bg-white text-slate-900 border border-slate-200 rounded-bl-md"
                }`}>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className={`text-[10px] mt-1 tabular-nums ${isMine ? "text-white/60" : "text-slate-400"}`}>
                    {new Date(m.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </main>

      <form onSubmit={send} className="bg-white border-t border-slate-200 sticky bottom-0">
        <div className="p-3 mx-auto max-w-2xl w-full flex items-center gap-2">
          <input
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder={`Message ${agent?.full_name?.split(" ")[0] || "your agent"}…`}
            className="flex-1 rounded-full border border-slate-300 px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-slate-900"
            data-testid="portal-chat-input"
          />
          <button
            type="submit"
            disabled={!body.trim() || sending}
            className="h-10 w-10 rounded-full flex items-center justify-center text-white shadow-md disabled:opacity-40 shrink-0"
            style={{ background: "var(--primary-hex)" }}
            data-testid="portal-chat-send"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
